/**
 * Device-capability detection.
 *
 * One module so the rest of the client never sniffs the user agent or window
 * features directly. Everything mobile-relevant (touch availability, coarse
 * pointer, save-data preference, effective network type) is funneled through
 * here. Functions are pure (`navigator` reads only) and safe to call before
 * any rendering work.
 */

/** True when the device exposes any touch points. */
export function hasTouch(): boolean {
	if (typeof navigator === "undefined") return false;
	return navigator.maxTouchPoints > 0;
}

/** True when the primary pointer is coarse (a finger, generally). */
export function hasCoarsePointer(): boolean {
	if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
		return false;
	}
	return window.matchMedia("(pointer: coarse)").matches;
}

/** True when the device should prefer touch-first UI affordances. */
export function isTouchPrimary(): boolean {
	return hasTouch() && hasCoarsePointer();
}

/** Best-effort detection of an integrated mobile GPU based on the WebGL
 *  unmasked-renderer string. We treat it as advisory only; the runtime
 *  benchmark in QualityPresets is the actual deciding signal. */
export function isLikelyMobileGpu(): boolean {
	try {
		const canvas = document.createElement("canvas");
		const gl =
			(canvas.getContext("webgl2") as WebGL2RenderingContext | null) ??
			(canvas.getContext("webgl") as WebGLRenderingContext | null);
		if (!gl) return false;
		const dbg = gl.getExtension("WEBGL_debug_renderer_info");
		const renderer = dbg
			? (gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) as string)
			: "";
		return /(Adreno|Mali|PowerVR|Apple GPU|Apple A\d+|Apple M\d+ GPU)/i.test(
			renderer,
		);
	} catch {
		return false;
	}
}

/** Network Information API hints; all fields optional because the API is
 *  not implemented on iOS Safari. */
export interface NetworkHints {
	saveData: boolean;
	effectiveType: "slow-2g" | "2g" | "3g" | "4g" | "unknown";
}

interface NetworkInformationLike {
	saveData?: boolean;
	effectiveType?: string;
}

export function readNetworkHints(): NetworkHints {
	const nav = (typeof navigator !== "undefined" ? navigator : undefined) as
		| (Navigator & { connection?: NetworkInformationLike })
		| undefined;
	const conn = nav?.connection;
	const t = conn?.effectiveType;
	const eff: NetworkHints["effectiveType"] =
		t === "slow-2g" || t === "2g" || t === "3g" || t === "4g" ? t : "unknown";
	return {
		saveData: Boolean(conn?.saveData),
		effectiveType: eff,
	};
}

/** True when the device should prefer a reduced snapshot rate. */
export function prefersReducedNetwork(): boolean {
	const h = readNetworkHints();
	if (h.saveData) return true;
	return h.effectiveType === "slow-2g" || h.effectiveType === "2g" || h.effectiveType === "3g";
}
